<template>
  <div class="linehaul-calculator">
    <!-- Page Header -->
    <div class="page-header">
      <v-container fluid class="px-6">
        <div class="d-flex align-center justify-space-between py-4">
          <div class="d-flex align-center">
            <div class="header-icon-badge mr-4">
              <v-icon size="20" class="header-icon-badge__icon">fas fa-route</v-icon>
            </div>
            <div>
              <div class="d-flex align-center">
                <h1 class="text-h5 font-weight-bold mb-0" style="color: #1a202c;">Line-haul Calculator</h1>
                <span class="header-tag ml-3">LOGISTICS</span>
              </div>
              <p class="body-2 mb-0 mt-1" style="color: #718096;">Route optimisation for production & consolidation depots</p>
            </div>
          </div>
          <div class="d-none d-md-flex align-center">
            <span class="header-date-chip">
              <v-icon size="12" class="mr-1">fas fa-clock</v-icon>
              {{ new Date().toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) }}
            </span>
          </div>
        </div>
      </v-container>
    </div>

    <v-container fluid class="px-6 pt-6 pb-10 page-body">
      <!-- Input Form Card -->
      <v-card class="mb-6 form-card" rounded="lg">
        <div class="form-card-accent"></div>
        <v-card-text class="pa-5 pb-4">
          <div class="d-flex align-center justify-space-between mb-4">
            <div class="d-flex align-center">
              <v-icon color="blue-grey darken-1" size="18" class="mr-2">fas fa-sliders-h</v-icon>
              <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Parameters</span>
            </div>
            <v-divider class="mx-4"></v-divider>
          </div>
          <v-row align="center" class="enterprise-inputs">
            <v-col cols="12" md="2" v-if="isInternalUser">
              <label class="field-label">Client</label>
              <v-autocomplete
                v-model="selectedClientId"
                :items="clients"
                item-text="name"
                item-value="id"
                placeholder="Select client"
                prepend-inner-icon="fas fa-building"
                outlined
                dense
                clearable
                hide-no-data
                hide-details="auto"
                :loading="clientsLoading"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="2">
              <label class="field-label">Tracking Number</label>
              <v-text-field
                v-model="trackingNumber"
                placeholder="e.g. ABC123"
                prepend-inner-icon="fas fa-barcode"
                outlined
                dense
                clearable
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <v-col cols="12" md="2">
              <label class="field-label">Production Depot(s)</label>
              <v-autocomplete
                v-model="selectedDepots"
                :items="wmsDepots"
                item-text="name"
                item-value="id"
                placeholder="Select depots"
                prepend-inner-icon="fas fa-warehouse"
                multiple
                chips
                small-chips
                deletable-chips
                clearable
                outlined
                dense
                :loading="depotsLoading"
                :disabled="!!trackingNumber"
                hide-details="auto"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="2">
              <label class="field-label">Delivery Suburb</label>
              <v-autocomplete
                v-model="deliverySuburb"
                :items="suburbSuggestions"
                :search-input.sync="suburbSearch"
                :loading="suburbLoading"
                item-text="display"
                item-value="display"
                placeholder="Suburb or postcode"
                prepend-inner-icon="fas fa-search-location"
                clearable
                outlined
                dense
                hide-no-data
                hide-details="auto"
                :disabled="!!trackingNumber"
                @change="onSuburbSelect"
              ></v-autocomplete>
            </v-col>

            <v-col cols="12" md="2">
              <label class="field-label">Earliest Departure</label>
              <DatePicker
                ref="earliestDeparturePicker"
                :date_props="earliestDeparture"
                @interface="onEarliestDepartureChange"
                :allowedDates="allowedEarliestDepartureDates"
                outlined
                dense
                clearable
                :hideDetails="'auto'"
                :disabled="!!trackingNumber"
              />
            </v-col>

            <v-col cols="12" md="2">
              <label class="field-label">Delivery Date</label>
              <DatePicker
                ref="deliveryDatePicker"
                :date_props="deliveryDate"
                @interface="onDeliveryDateChange"
                :allowedDates="allowedDeliveryDates"
                outlined
                dense
                clearable
                :hideDetails="'auto'"
                :disabled="!!trackingNumber"
              />
            </v-col>

            <v-col cols="12" md="2" class="d-flex align-end">
              <v-btn
                color="primary"
                :disabled="!canShowOptions"
                :loading="optionsLoading"
                @click="showOptions"
                outlined
                class="text-none font-weight-bold search-btn px-6"
                height="38"
              >
                <v-icon left small>fas fa-search</v-icon>
                Calculate
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Results -->
      <template v-if="hasResults">
        <!-- Results Summary Banner -->
        <v-card class="mb-5 summary-banner" rounded="lg" flat>
          <v-card-text class="pa-4">
            <v-row align="center" no-gutters>
              <v-col cols="12" md="8">
                <div class="d-flex align-center">
                  <div class="summary-badge mr-3">
                    <v-icon color="white" size="16">fas fa-check</v-icon>
                  </div>
                  <div>
                    <div class="body-2 font-weight-bold grey--text text--darken-4">
                      {{ routeOptions.length }} route{{ routeOptions.length !== 1 ? 's' : '' }} available
                    </div>
                    <div class="d-flex align-center mt-1 flex-wrap">
                      <span class="caption font-weight-medium grey--text text--darken-2 mr-2">{{ resultOriginDepotName }}</span>
                      <v-icon x-small color="blue-grey lighten-1" class="mx-1">fas fa-arrow-right</v-icon>
                      <span class="caption font-weight-medium grey--text text--darken-2 ml-2">{{ resultDestinationDepotName }}</span>
                    </div>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-md-right mt-3 mt-md-0">
                <span class="caption grey--text">Select a row to expand</span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Results Table -->
        <v-card class="mb-5 table-card" rounded="lg" flat>
          <v-data-table
            :headers="tableHeaders"
            :items="routeOptions"
            :items-per-page="10"
            sort-by="creation_cutoff"
            :sort-desc="false"
            class="routes-table"
            :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          >
            <template v-slot:item="{ item }">
              <tr
                @click="toggleDetails(item)"
                :class="{ 'selected-row': selectedOption === item }"
                class="table-row"
              >
                <td class="text-no-wrap">{{ item.creation_cutoff || '-' }}</td>
                <td class="text-no-wrap">{{ item.latest_consolidation_date || '-' }}</td>
                <td class="text-no-wrap">{{ item._departure_date || '-' }}</td>
                <td class="text-no-wrap">{{ item._arrival_date || '-' }}</td>
                <td class="text-no-wrap">{{ item.delivery_ready_date || '-' }}</td>
                <td>
                  <span class="legs-badge">{{ item.depots ? item.depots.length - 1 : '-' }}</span>
                </td>
                <td>
                  <span class="font-weight-bold" :class="item.price != null ? 'teal--text text--darken-2' : 'grey--text'">
                    {{ item.price != null ? '$' + item.price : '--' }}
                  </span>
                </td>
                <td>
                  <v-icon size="18" color="blue-grey lighten-1" class="expand-icon">{{ selectedOption === item ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card>

        <!-- Detail Panel -->
        <v-expand-transition>
          <v-card v-if="selectedOption" class="mb-5 detail-card" rounded="lg" flat>
            <div class="detail-card-header pa-4">
              <div class="d-flex align-center">
                <v-icon color="primary" size="16" class="mr-2">fas fa-info-circle</v-icon>
                <span class="subtitle-1 font-weight-bold" style="color: #1a202c;">Route Details</span>
                <v-spacer></v-spacer>
                <v-btn icon small @click="selectedOption = null">
                  <v-icon size="18" color="grey darken-1">mdi-close</v-icon>
                </v-btn>
              </div>
            </div>

            <v-card-text class="pa-5">
              <!-- Stat Cards Row -->
              <v-row class="mb-1">
                <v-col cols="12" sm="6" md="4">
                  <div class="stat-card stat-card--indigo">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="16">fas fa-calendar-check</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Production Cutoff</div>
                      <div class="stat-card__value">{{ selectedOption.creation_cutoff || '-' }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="stat-card stat-card--blue">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="16">fas fa-boxes</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Latest Consolidation</div>
                      <div class="stat-card__value">{{ selectedOption.latest_consolidation_date || '-' }}</div>
                    </div>
                  </div>
                </v-col>
                <v-col cols="12" sm="6" md="4">
                  <div class="stat-card stat-card--teal">
                    <div class="stat-card__icon">
                      <v-icon color="white" size="16">fas fa-truck-loading</v-icon>
                    </div>
                    <div class="stat-card__content">
                      <div class="stat-card__label">Delivery Ready</div>
                      <div class="stat-card__value">{{ selectedOption.delivery_ready_date || '-' }}</div>
                    </div>
                  </div>
                </v-col>
              </v-row>

              <v-divider class="my-5"></v-divider>

              <!-- Line-haul Map -->
              <div class="section-header mb-3">
                <v-icon color="blue-grey darken-1" size="16" class="mr-2">fas fa-map-marked-alt</v-icon>
                <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Route Map</span>
              </div>
              <v-card outlined rounded="lg" class="mb-6 map-container overflow-hidden">
                <div style="height: 95vh; position: relative;">
                  <div v-if="mapLoading" class="map-loading-overlay">
                    <v-progress-circular indeterminate color="primary" size="56" width="5"></v-progress-circular>
                    <div class="mt-3 body-1 grey--text text--darken-1">Loading Line-haul map...</div>
                  </div>
                  <v-btn
                    v-if="initialBounds"
                    fab
                    small
                    color="white"
                    class="reset-map-btn"
                    @click="resetMap"
                    title="Reset map view"
                  >
                    <v-icon>mdi-crosshairs-gps</v-icon>
                  </v-btn>
                  <l-map
                    ref="routeMap"
                    :zoom="4"
                    :center="[-25.5, 134.5]"
                    :min-zoom="4"
                    :max-zoom="18"
                    :max-bounds="mapBounds"
                    :max-bounds-viscosity="1.0"
                    :options="{ zoomControl: true, attributionControl: true }"
                    style="height: 100%; width: 100%; z-index: 1;"
                    @ready="onRouteMapReady"
                  >
                    <l-tile-layer :url="tileUrl"></l-tile-layer>
                    <l-polyline
                      v-for="(leg, lIndex) in routeMapLegs"
                      :key="'leg-' + lIndex"
                      :lat-lngs="leg.path"
                      :color="leg.color"
                      :weight="3"
                      :opacity="0.8"
                    ></l-polyline>
                    <l-marker
                      v-for="(leg, lIndex) in routeMapLegs"
                      :key="'legnum-' + lIndex"
                      :lat-lng="leg.midpoint"
                      :icon="createLegIcon(lIndex + 1, leg.color)"
                    ></l-marker>
                    <l-marker
                      v-for="(leg, lIndex) in routeMapLegs"
                      :key="'arrow-' + lIndex"
                      :lat-lng="leg.arrowPoint"
                      :icon="createArrowIcon(leg.bearing, leg.color)"
                    ></l-marker>
                    <l-marker
                      v-for="(coords, mIndex) in routeMapMarkers"
                      :key="'marker-' + mIndex"
                      :lat-lng="coords.latLng"
                      :icon="createDepotIcon(mIndex, routeMapMarkers.length)"
                    >
                      <l-tooltip :options="{ permanent: true, direction: tooltipDirection(mIndex), offset: tooltipOffset(mIndex) }">
                        {{ coords.name }} {{ coords.dateLabel }}
                      </l-tooltip>
                    </l-marker>
                  </l-map>
                </div>
              </v-card>

              <v-divider class="my-5"></v-divider>

              <!-- Line-haul Hops -->
              <div class="section-header mb-3">
                <v-icon color="blue-grey darken-1" size="16" class="mr-2">fas fa-project-diagram</v-icon>
                <span class="subtitle-2 font-weight-bold blue-grey--text text--darken-2 text-uppercase" style="letter-spacing: 0.8px;">Route Legs</span>
                <v-chip x-small color="blue-grey lighten-4" class="ml-3 font-weight-bold blue-grey--text text--darken-2">
                  {{ selectedOptionDepots.length }} stops
                </v-chip>
                <v-chip x-small outlined color="blue-grey" class="ml-2 font-weight-bold">
                  {{ selectedOption.depots ? selectedOption.depots.length - 1 : '-' }} legs
                </v-chip>
              </div>
              <v-timeline dense align-top v-if="selectedOptionDepots.length">
                <v-timeline-item
                  v-for="(hop, hIndex) in selectedOptionDepots"
                  :key="hIndex"
                  color="transparent"
                  large
                >
                  <template v-slot:icon>
                    <div
                      class="outline-dot-circle"
                      :style="{ borderColor: hopColorHex(hIndex), color: hopColorHex(hIndex) }"
                    >{{ hIndex + 1 }}</div>
                  </template>
                  <v-card class="hop-card" outlined rounded="lg">
                    <v-card-text class="pa-4">
                      <div class="d-flex align-center justify-space-between flex-wrap">
                        <div class="d-flex align-center">
                          <div>
                            <div class="d-flex align-center mb-1">
                              <span class="subtitle-1 font-weight-bold grey--text text--darken-4">
                                {{ getDepotName(hop.depot_id) || ('Depot ' + hop.depot_id) }}
                              </span>
                              <v-chip
                                x-small
                                :color="hopColor(hIndex)"
                                dark
                                class="ml-2 font-weight-bold"
                              >
                                {{ hopLabel(hIndex) }}
                              </v-chip>
                            </div>
                          </div>
                        </div>
                        <div class="d-flex align-center mt-2 mt-sm-0">
                          <div v-if="hop.departure_date" class="hop-date-badge mr-3">
                            <v-icon x-small color="blue darken-1" class="mr-1">fas fa-truck</v-icon>
                            <span class="caption font-weight-bold blue--text text--darken-1">{{ hop.departure_date }}</span>
                          </div>
                          <div v-if="hop.arrival_date" class="hop-date-badge">
                            <v-icon x-small color="green darken-1" class="mr-1">fas fa-truck-loading</v-icon>
                            <span class="caption font-weight-bold green--text text--darken-1">{{ hop.arrival_date }}</span>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-expand-transition>
      </template>

      <!-- No results message -->
      <v-card v-if="noResults" class="mb-4" rounded="lg">
        <v-card-text class="pa-6">
          <v-alert type="warning" prominent outlined class="mb-0">
            <div class="body-1">No line-haul routes found for the selected criteria. Please try different parameters.</div>
          </v-alert>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import axios from 'axios';
import L from 'leaflet';
import { LMap, LTileLayer, LMarker, LTooltip, LPolyline } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';
import DatePicker from '../../parts/DatePicker.vue';

export default {
  name: "LinehaulCalculator",
  components: {
    DatePicker,
    LMap,
    LTileLayer,
    LMarker,
    LTooltip,
    LPolyline,
  },
  data() {
    return {
      // Form inputs
      trackingNumber: null,
      selectedDepots: [],
      selectedClientId: null,
      deliverySuburb: null,
      selectedSuburbData: null,
      suburbSearch: null,
      deliveryDate: null,
      earliestDeparture: null,

      // Client data
      clients: [],
      clientsLoading: false,

      // Loading states
      depotsLoading: false,
      suburbLoading: false,
      optionsLoading: false,

      // Depot data
      allDepots: [],

      // Suburb autocomplete
      suburbSuggestions: [],

      // Results
      routeOptions: [],
      hasResults: false,
      noResults: false,

      // Detail panel
      selectedOption: null,
      depotCoordsCache: {},
      routeMapMarkers: [],
      routeMapLegs: [],
      mapLoading: false,
      initialBounds: null,
      tileUrl: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',

      // Table headers
      tableHeaders: [
        { text: 'Production Cutoff', value: 'creation_cutoff', sortable: true },
        { text: 'Latest Consolidation', value: 'latest_consolidation_date', sortable: true },
        { text: 'Departure', value: '_departure_date', sortable: true },
        { text: 'Arrival', value: '_arrival_date', sortable: true },
        { text: 'Delivery Ready', value: 'delivery_ready_date', sortable: true },
        { text: 'Total Legs', value: 'total_legs', sortable: true },
        { text: 'Price', value: 'price', sortable: true },
        { text: '', value: 'actions', sortable: false, width: '48px' },
      ],
    };
  },
  computed: {
    isInternalUser() {
      return this.$store.getters['auth/isInternalUser'];
    },
    wmsDepots() {
      return this.allDepots
        .filter(d => d.wms)
        .sort((a, b) => (a.name > b.name) ? 1 : -1);
    },
    canShowOptions() {
      if (this.trackingNumber && this.trackingNumber.trim()) return true;
      return this.selectedDepots.length > 0 && this.deliveryDate && this.selectedSuburbData;
    },
    selectedOptionDepots() {
      if (!this.selectedOption || !this.selectedOption.depots || !Array.isArray(this.selectedOption.depots)) return [];
      return this.selectedOption.depots;
    },
    mapBounds() {
      return L.latLngBounds([-10, 110], [-48, 179]);
    },
    resultOriginDepotName() {
      if (!this.routeOptions.length) return '';
      const depots = this.routeOptions[0].depots;
      if (!depots || !depots.length) return '';
      return this.getDepotName(depots[0].depot_id) || ('Depot ' + depots[0].depot_id);
    },
    resultDestinationDepotName() {
      if (!this.routeOptions.length) return '';
      const depots = this.routeOptions[0].depots;
      if (!depots || !depots.length) return '';
      const last = depots[depots.length - 1];
      return this.getDepotName(last.depot_id) || ('Depot ' + last.depot_id);
    },
  },
  mounted() {
    this.fetchDepots();
    if (this.isInternalUser) {
      this.fetchClients();
    }
  },
  watch: {
    suburbSearch(val) {
      if (!val || val.length < 2) return;
      if (this.suburbLoading) return;
      if (this.deliverySuburb && this.deliverySuburb === val) return;
      this.searchSuburbs(val);
    },
    selectedOption() {
      this.$nextTick(() => this.updateRouteMap());
    },
  },
  methods: {
    getDepartureDate(item) {
      if (!item || !item.depots || !item.depots.length) return null;
      return item.depots[0].departure_date || null;
    },
    getArrivalDate(item) {
      if (!item || !item.depots || !item.depots.length) return null;
      const lastHop = item.depots[item.depots.length - 1];
      return lastHop.arrival_date || null;
    },
    hopColor(index) {
      if (index === 0) return 'green';
      if (index === this.selectedOptionDepots.length - 1) return 'red darken-1';
      return 'orange darken-1';
    },
    hopColorHex(index) {
      if (index === 0) return '#4CAF50';
      if (index === this.selectedOptionDepots.length - 1) return '#E53935';
      return '#FB8C00';
    },
    hopDotClass(index) {
      if (index === 0) return 'green';
      if (index === this.selectedOptionDepots.length - 1) return 'red';
      return 'orange';
    },
    hopLabel(index) {
      if (index === 0) return 'Origin';
      if (index === this.selectedOptionDepots.length - 1) return 'Destination';
      return 'Transit';
    },
    onDeliveryDateChange(date) {
      this.deliveryDate = date;
    },
    onEarliestDepartureChange(date) {
      this.earliestDeparture = date;
    },
    allowedEarliestDepartureDates(val) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const d = new Date(val + 'T00:00:00');
      return d >= today;
    },
    allowedDeliveryDates(val) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const maxDate = new Date(today);
      maxDate.setDate(maxDate.getDate() + 14);
      const d = new Date(val + 'T00:00:00');
      return d >= today && d <= maxDate;
    },
    onSuburbSelect(displayVal) {
      if (!displayVal) {
        this.selectedSuburbData = null;
        return;
      }
      this.selectedSuburbData = this.suburbSuggestions.find(s => s.display === displayVal) || null;
    },
    async searchSuburbs(query) {
      this.suburbLoading = true;
      try {
        const response = await axios.get(process.env.API_URL + '/zones/autocomplete?s=' + encodeURIComponent(query));
        if (response.data && Array.isArray(response.data)) {
          this.suburbSuggestions = response.data.map(item => {
            const parsed = {};
            if (item.suburb) {
              const parts = item.suburb.split(',').map(p => p.trim());
              parsed.suburb_name = parts[0] || null;
              parsed.postcode = parts[1] || null;
            }
            return {
              ...item,
              ...parsed,
              display: item.suburb || '',
            };
          });
        }
      } catch (error) {
        this.suburbSuggestions = [];
      } finally {
        this.suburbLoading = false;
      }
    },
    getDepotName(depotId) {
      const depot = this.allDepots.find(d => d.id === depotId);
      return depot ? depot.name : null;
    },
    rowClass(item) {
      return this.selectedOption === item ? 'selected-row' : '';
    },
    async fetchClients() {
      this.clientsLoading = true;
      try {
        const response = await axios.get(process.env.API_URL + '/clients');
        if (response.status === 200) {
          this.clients = response.data.sort((a, b) => (a.name > b.name) ? 1 : -1);
        }
      } catch (error) {
        this.$store.commit('displaySnackbar', 'Failed to load clients');
      } finally {
        this.clientsLoading = false;
      }
    },
    async fetchDepots() {
      this.depotsLoading = true;
      try {
        const response = await axios.get(process.env.API_URL + '/depots');
        if (response.status === 200) {
          this.allDepots = response.data;
        }
      } catch (error) {
        this.$store.commit('displaySnackbar', 'Failed to load depots');
      } finally {
        this.depotsLoading = false;
      }
    },
    async showOptions() {
      this.optionsLoading = true;
      this.hasResults = false;
      this.noResults = false;
      this.selectedOption = null;
      this.routeOptions = [];

      try {
        let params;
        if (this.trackingNumber && this.trackingNumber.trim()) {
          params = { tracking: this.trackingNumber.trim() };
        } else {
          params = {
            origin_depots: JSON.stringify(this.selectedDepots),
            delivery_date: this.deliveryDate,
            postcode: this.selectedSuburbData.postcode,
            suburb: this.selectedSuburbData.suburb_name,
          };
          if (this.earliestDeparture) {
            params.earliest_departure = this.earliestDeparture;
          }
        }
        if (this.isInternalUser && this.selectedClientId) {
          params.client_id = this.selectedClientId;
        }
        const response = await axios.get(process.env.API_URL + '/linehauls/routes', { params });

        if (response.status === 200 && response.data) {
          const data = response.data;
          const routes = Array.isArray(data) ? data : (data.routes || data.options || []);

          if (routes.length > 0) {
            this.routeOptions = routes.map(route => {
              const r = { ...route };
              r._departure_date = this.getDepartureDate(r);
              r._arrival_date = this.getArrivalDate(r);
              r.total_legs = r.depots ? r.depots.length - 1 : 0;
              return r;
            });
            this.hasResults = true;
          } else {
            this.noResults = true;
          }
        } else {
          this.noResults = true;
        }
      } catch (error) {
        console.error('Linehaul calculator error:', error);
        const msg = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : 'Failed to fetch linehaul routes';
        this.$store.commit('displaySnackbar', 'ERROR: ' + msg);
        this.noResults = true;
      } finally {
        this.optionsLoading = false;
      }
    },
    onRouteMapReady() {
      if (this.selectedOption) this.updateRouteMap();
    },
    tooltipDirection(index) {
      const directions = ['top', 'bottom', 'right', 'left'];
      return directions[index % directions.length];
    },
    tooltipOffset(index) {
      const offsets = [[0, -14], [0, 14], [14, 0], [-14, 0]];
      return offsets[index % offsets.length];
    },
    createDepotIcon(index, total) {
      const color = index === 0 ? '#4CAF50' : (index === total - 1 ? '#E53935' : '#FB8C00');
      const size = 16;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 1}" fill="${color}" stroke="#fff" stroke-width="2"/>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    createLegIcon(legNumber, color) {
      const size = 28;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <circle cx="${size/2}" cy="${size/2}" r="${size/2}" fill="${color}"/>
        <text x="${size/2}" y="${size/2 + 1}" text-anchor="middle" dominant-baseline="central" fill="#fff" font-size="12" font-weight="bold" font-family="Arial">${legNumber}</text>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    createArrowIcon(bearing, color) {
      const size = 22;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
        <polygon points="11,2 19,19 11,14 3,19" fill="${color}" transform="rotate(${bearing}, 11, 11)"/>
      </svg>`;
      return L.divIcon({
        className: '',
        html: svg,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
    getBearing(from, to) {
      const dLng = (to[1] - from[1]) * Math.PI / 180;
      const lat1 = from[0] * Math.PI / 180;
      const lat2 = to[0] * Math.PI / 180;
      const y = Math.sin(dLng) * Math.cos(lat2);
      const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);
      return ((Math.atan2(y, x) * 180 / Math.PI) + 360) % 360;
    },
    getDepotCoords(depotId) {
      if (this.depotCoordsCache[depotId]) return this.depotCoordsCache[depotId];
      const depot = this.allDepots.find(d => d.id == depotId);
      if (!depot) return null;
      const lat = parseFloat(depot.latitude);
      const lng = parseFloat(depot.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      const coords = { lat, lng };
      this.depotCoordsCache[depotId] = coords;
      return coords;
    },
    async fetchRoadRoute(from, to) {
      try {
        const url = `https://router.project-osrm.org/route/v1/driving/${from[1]},${from[0]};${to[1]},${to[0]}?overview=full&geometries=geojson`;
        const response = await fetch(url);
        const data = await response.json();
        if (data.code === 'Ok' && data.routes && data.routes.length > 0) {
          return data.routes[0].geometry.coordinates.map(c => [c[1], c[0]]);
        }
      } catch (e) {
        console.warn('OSRM routing failed, falling back to straight line', e);
      }
      return [from, to];
    },
    pointAlongPath(path, fraction) {
      if (path.length < 2) return path[0];
      const lengths = [];
      let total = 0;
      for (let i = 1; i < path.length; i++) {
        const dx = path[i][0] - path[i - 1][0];
        const dy = path[i][1] - path[i - 1][1];
        const seg = Math.sqrt(dx * dx + dy * dy);
        lengths.push(seg);
        total += seg;
      }
      let target = total * fraction;
      for (let i = 0; i < lengths.length; i++) {
        if (target <= lengths[i]) {
          const t = lengths[i] > 0 ? target / lengths[i] : 0;
          return [
            path[i][0] + (path[i + 1][0] - path[i][0]) * t,
            path[i][1] + (path[i + 1][1] - path[i][1]) * t,
          ];
        }
        target -= lengths[i];
      }
      return path[path.length - 1];
    },
    async updateRouteMap() {
      this.routeMapMarkers = [];
      this.routeMapLegs = [];
      this.mapLoading = false;
      this.initialBounds = null;

      if (!this.selectedOption || !this.selectedOptionDepots.length) return;

      this.mapLoading = true;

      const depots = this.selectedOptionDepots;
      const coords = [];
      for (let i = 0; i < depots.length; i++) {
        const d = depots[i];
        const c = this.getDepotCoords(d.depot_id);
        if (c) {
          const name = this.getDepotName(d.depot_id) || ('Depot ' + d.depot_id);
          const isLast = i === depots.length - 1;
          const dateLabel = isLast
            ? (d.arrival_date ? '(arr: ' + d.arrival_date + ')' : '')
            : (d.departure_date ? '(dep: ' + d.departure_date + ')' : '');
          coords.push({ latLng: [c.lat, c.lng], name, dateLabel });
        }
      }

      this.routeMapMarkers = coords;

      const legColors = ['#1565C0', '#E53935', '#2E7D32', '#F57C00', '#6A1B9A', '#00838F'];
      const legs = [];
      for (let i = 0; i < coords.length - 1; i++) {
        const from = coords[i].latLng;
        const to = coords[i + 1].latLng;
        const path = await this.fetchRoadRoute(from, to);
        const midpoint = this.pointAlongPath(path, 0.5);
        const arrowPoint = this.pointAlongPath(path, 0.75);
        legs.push({
          path,
          color: legColors[i % legColors.length],
          midpoint,
          arrowPoint,
          bearing: this.getBearing(from, to),
        });
      }
      this.routeMapLegs = legs;

      await this.$nextTick();
      const map = this.$refs.routeMap ? this.$refs.routeMap.mapObject : null;
      if (map && coords.length >= 2) {
        const bounds = L.latLngBounds(coords.map(c => c.latLng));
        this.initialBounds = bounds;
        map.fitBounds(bounds, { padding: [40, 40] });
      }
      this.mapLoading = false;
    },
    resetMap() {
      const map = this.$refs.routeMap ? this.$refs.routeMap.mapObject : null;
      if (map && this.initialBounds) {
        map.fitBounds(this.initialBounds, { padding: [40, 40] });
      }
    },
    toggleDetails(item) {
      if (this.selectedOption === item) {
        this.selectedOption = null;
      } else {
        this.selectedOption = item;
      }
    },
  }
};
</script>

<style scoped>
.linehaul-calculator {
  background: #f5f6f8;
  min-height: 100vh;
}

/* ── Header ── */
.page-header {
  background: #fff;
  border-bottom: 1px solid #e2e5ea;
}

.header-icon-badge {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: transparent;
  border: 1.5px solid #1976d2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon-badge__icon {
  color: #1976d2;
}

.header-tag {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 2px 8px;
  border-radius: 3px;
  border: 1px solid #1976d2;
  color: #1976d2;
  background: transparent;
}

.header-date-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 4px;
  border: 1px solid #d2d6dc;
  color: #5f6b7a;
  background: transparent;
}

.header-date-chip .v-icon {
  color: #9ca3af !important;
}

/* ── Form Card ── */
.form-card {
  position: relative;
  overflow: hidden;
  border: 1px solid #e2e5ea;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.form-card-accent {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, #1976d2, transparent 60%);
}

/* ── Field Labels ── */
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #5f6b7a;
  margin-bottom: 4px;
}

/* ── Enterprise Inputs ── */
::v-deep .enterprise-inputs .v-input--is-outlined .v-input__slot {
  background: #fff !important;
  border-radius: 6px !important;
  min-height: 38px !important;
}

::v-deep .enterprise-inputs .v-input--is-outlined fieldset {
  border-color: #d2d6dc !important;
  border-width: 1px !important;
  transition: border-color 0.15s ease;
}

::v-deep .enterprise-inputs .v-input--is-focused fieldset {
  border-color: #1976d2 !important;
  border-width: 1.5px !important;
}

::v-deep .enterprise-inputs .v-input--is-outlined:hover fieldset {
  border-color: #9ca3af !important;
}

::v-deep .enterprise-inputs .v-input__prepend-inner .v-icon {
  font-size: 13px !important;
  color: #9ca3af !important;
}

::v-deep .enterprise-inputs .v-input--is-focused .v-input__prepend-inner .v-icon {
  color: #1976d2 !important;
}

::v-deep .enterprise-inputs .v-text-field input,
::v-deep .enterprise-inputs .v-select__selection {
  font-size: 13px !important;
  color: #2d3748 !important;
}

::v-deep .enterprise-inputs .v-text-field input::placeholder {
  color: #a0aec0 !important;
  font-size: 13px !important;
}

::v-deep .enterprise-inputs .v-label {
  display: none !important;
}

.search-btn {
  border-radius: 6px !important;
  letter-spacing: 0.2px;
  font-size: 13px !important;
  box-shadow: none !important;
  transition: all 0.15s ease;
  border-width: 1.5px !important;
}

.search-btn:hover:not(:disabled) {
  background: rgba(25, 118, 210, 0.04) !important;
}

/* ── Summary Banner ── */
.summary-banner {
  border: 1px solid #e2e5ea;
  border-left: 3px solid #2e7d32;
  background: #fff;
}

.summary-badge {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: transparent;
  border: 2px solid #2e7d32;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.summary-badge .v-icon {
  color: #2e7d32 !important;
}

/* ── Table ── */
.table-card {
  border: 1px solid #e2e5ea;
  overflow: hidden;
}

::v-deep .routes-table .v-data-table-header {
  background: #f8f9fa;
}

::v-deep .routes-table .v-data-table-header th {
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #5f6b7a !important;
  border-bottom: 1px solid #e2e5ea !important;
  padding-top: 12px !important;
  padding-bottom: 12px !important;
  white-space: nowrap;
}

::v-deep .routes-table tbody td {
  font-size: 13px !important;
  padding-top: 11px !important;
  padding-bottom: 11px !important;
  color: #2d3748;
  font-variant-numeric: tabular-nums;
}

::v-deep .routes-table tbody tr {
  cursor: pointer;
  transition: background-color 0.1s ease;
}

::v-deep .routes-table tbody tr:hover {
  background-color: #f0f4f8 !important;
}

.table-row.selected-row {
  background-color: #e8f0fe !important;
  box-shadow: inset 3px 0 0 #1565c0;
}

.table-row.selected-row:hover {
  background-color: #d4e4fc !important;
}

.legs-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 22px;
  border-radius: 4px;
  background: transparent;
  border: 1px solid #cbd5e0;
  font-size: 12px;
  font-weight: 700;
  color: #4a5568;
  padding: 0 6px;
}

.expand-icon {
  transition: transform 0.15s ease;
}

/* ── Detail Panel ── */
.detail-card {
  border: 1px solid #e2e5ea;
  border-top: 2px solid #1976d2;
  overflow: hidden;
}

.detail-card-header {
  background: #fff;
  border-bottom: 1px solid #e2e5ea;
}

/* ── Stat Cards ── */
.stat-card {
  display: flex;
  align-items: center;
  padding: 14px 16px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e2e5ea;
  transition: border-color 0.15s ease;
}

.stat-card:hover {
  border-color: #cbd5e0;
}

.stat-card__icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 14px;
  flex-shrink: 0;
  background: transparent;
}

.stat-card--blue .stat-card__icon { border: 1.5px solid #1565c0; }
.stat-card--blue .stat-card__icon .v-icon { color: #1565c0 !important; }
.stat-card--teal .stat-card__icon { border: 1.5px solid #00796b; }
.stat-card--teal .stat-card__icon .v-icon { color: #00796b !important; }
.stat-card--indigo .stat-card__icon { border: 1.5px solid #37474f; }
.stat-card--indigo .stat-card__icon .v-icon { color: #37474f !important; }
.stat-card--green .stat-card__icon { border: 1.5px solid #2e7d32; }
.stat-card--green .stat-card__icon .v-icon { color: #2e7d32 !important; }

.stat-card__label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #718096;
  margin-bottom: 2px;
}

.stat-card__value {
  font-size: 15px;
  font-weight: 700;
  color: #1a202c;
  font-variant-numeric: tabular-nums;
}

/* ── Section Headers ── */
.section-header {
  display: flex;
  align-items: center;
}

/* ── Map ── */
.map-container {
  border: 1px solid #e2e5ea !important;
}

.reset-map-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2) !important;
}

.map-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.9);
}

/* ── Hop Cards ── */
.hop-card {
  border: 1px solid #e2e5ea !important;
  transition: border-color 0.15s ease;
}

.hop-card:hover {
  border-color: #cbd5e0 !important;
}

.hop-date-badge {
  display: inline-flex;
  align-items: center;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  padding: 3px 8px;
}

/* ── Page body ── */
.page-body {
  max-width: 1440px;
  margin: 0 auto;
}
</style>

<style>
.linehaul-calculator .v-timeline-item__dot--large {
  box-shadow: none !important;
}

.linehaul-calculator .v-timeline-item__dot--large .v-timeline-item__inner-dot {
  background: transparent !important;
}

.outline-dot-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2.5px solid currentColor;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-weight: 700;
}
</style>



// WEBPACK FOOTER //
// src/components/pages/Delivery/LinehaulCalculator.vue